import React from 'react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import TaskWidget, {
  APITaskData
} from '@SonarCX/components/molecules/TaskWidget'
import TaskRow from './TaskRow'

import { selectTaskStatus, fetchStatus } from '@SonarCX/slices/taskStatus'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import { useAuth } from '@SonarCX/providers/AuthProvider'
import { useStats } from '@SonarCX/providers/StatsProvider'

const OpenTasks = () => {
  const dispatch = useAppDispatch()

  const {
    statuses,
    loaded: taskStatusLoaded,
    loading: taskStatusLoading
  } = useAppSelector(selectTaskStatus)

  const { user, loaded: envLoaded } = useEnvironment()
  const { isLoggedIn } = useAuth()
  const { taskLoaded, taskStats } = useStats()

  const userID = React.useMemo(() => user?.id, [user])
  const canLoad = React.useMemo(
    () => isLoggedIn && envLoaded && Boolean(userID),
    [isLoggedIn, envLoaded, userID]
  )

  React.useEffect(() => {
    if (!taskStatusLoaded && !taskStatusLoading) {
      dispatch(fetchStatus())
    }
  }, [taskStatusLoaded, taskStatusLoading, dispatch])

  const getKey = React.useCallback(
    (pageIndex: number, previousPageData: APITaskData) => {
      if (!canLoad) {
        return null
      }

      // reached the end
      if (previousPageData && !previousPageData.data) return null

      // first page, we don't have `previousPageData`
      if (pageIndex === 0) return `taskDashboard:${user?.id}`

      // add the cursor to the API endpoint
      // @ts-ignore
      return `taskDashboard:${user?.id}:${previousPageData.pagination.next}`
    },
    [canLoad, user]
  )

  const formatTasks = React.useCallback(
    (tasks: APITask[]) => {
      return tasks.map(task => {
        const status: string = task.status_id
          ? statuses.find(status => status.id === task.status_id)?.name ??
            'Unknown Status'
          : 'Unassigned'
        return { ...task, status } as APITask & { status: string }
      })
    },
    [statuses]
  )

  return (
    <TaskWidget
      title={`Tasks Assigned To Me${taskLoaded ? ` (${taskStats?.open})` : ''}`}
      getKey={getKey}
      format={formatTasks}
      rowRenderer={TaskRow}
      noTasksText='No open tasks'
    />
  )
}

export default OpenTasks
